import L from 'leaflet';
import pako from 'pako';
import 'leaflet/dist/leaflet.css';
import { Info } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { FaBus, FaExchangeAlt, FaMapMarkerAlt, FaPlane, FaTaxi, FaTrain } from 'react-icons/fa';
import { MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Logo from '../components/common/Logo';
import '../components/modal/RouteMapModal.css';
import './SharedMapPage.css';
import CabOptions, { defaultCabCategory, selectedCabPrice } from '../components/search-form/results/CabOptions';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import { FreeMode, Keyboard, Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { getAuth } from 'firebase/auth';
import FallbackErrorModal from '../components/modal/FallbackErrorModal';
import LoadingModal from '../components/modal/LoadingModal';
import ShareMapModal from '../components/modal/ShareMapModal';
import { useAuth } from '../context/AuthContext';
import { fetchFareQuote } from '../services/flightBookingAPI';

// Helper Functions
const formatTime = (val) => {
    if (!val) return null;
    if (val.toString().includes('T')) {
        try {
            const d = new Date(val);
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        } catch (e) { return val; }
    }
    return val;
};

const calculateLayover = (arr, dep) => {
    if (!arr || !dep) return null;
    try {
        const getMin = (t) => {
            if (t.includes('T')) return new Date(t).getTime() / 60000;
            const [h, m] = t.split(':').map(Number);
            return h * 60 + m;
        };
        let diff = getMin(dep) - getMin(arr);
        if (diff <= 0) diff += 24 * 60; // Handle next day departure
        return diff < 60 ? `${diff}m` : `${Math.floor(diff / 60)}h ${diff % 60}m`;
    } catch (e) { return null; }
};

const compareClasses = (c1, c2) => {
    if (!c1 || !c2) return false;
    const normalize = (s) => String(s).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    return normalize(c1) === normalize(c2);
};

const SeatAvailabilityGrid = ({ leg, refreshedInfo, refreshingIdx, handleRefresh, actionButton }) => {
    const availableClasses = leg?.seat_classes?.filter(cls => cls !== 'GN') || [];
    const getDefaultClass = (classes) => {
        const ui = leg?.seat_class_ui;
        if (ui && classes.some(c => compareClasses(c, ui))) return ui;
        return classes.length > 0 ? classes[0] : null;
    };
    const [selectedClass, setSelectedClass] = useState(() => getDefaultClass(availableClasses));

    useEffect(() => {
        const classes = leg?.seat_classes?.filter(cls => cls !== 'GN') || [];
        const ui = leg?.seat_class_ui;
        if (ui && classes.some(c => compareClasses(c, ui))) {
            setSelectedClass(ui);
        } else if (classes.length > 0) {
            setSelectedClass(classes[0]);
        }
    }, [leg?.seat_class_ui, leg?.seat_classes]);

    if (availableClasses.length === 0) return null;

    const getClassData = (cls, idx) => {
        const uniqueKey = `${leg.train_num}_${idx}`;
        const refreshed = refreshedInfo[uniqueKey];

        const uiMatch = leg.seat_avail_ui && compareClasses(leg.seat_avail_ui.seat_class, cls);
        let availInfo = refreshed ? refreshed.avl : (uiMatch ? leg.seat_avail_ui.avl : (leg.seat_avail?.[cls] || null));
        let cost = refreshed ? refreshed.fare : (leg.seat_costs?.[cls] || null);

        if (!cost || cost === '--') {
            if (uiMatch) {
                cost = leg.seat_avail_ui.fare || null;
            }
        }

        let foundQuota = refreshed ? refreshed.quota : (uiMatch ? leg.seat_avail_ui.quota : null);
        let displayCls = cls;
        let hasData = (cost && cost !== '--') || (availInfo && availInfo !== 'N/A' && availInfo !== null);
        let displayQuota = hasData ? (foundQuota || leg.category || 'GN') : 'N/A';

        const availLower = availInfo?.toLowerCase() || '';
        const isAvbl = (availLower.includes('avail') || availLower.includes('avl') || availLower.includes('avbl')) && !availLower.includes('not');
        const isRac = availLower.includes('rac');
        const isNotAvail = availLower.includes('not') || availLower.includes('regret') || (availLower.includes('wl') && !isAvbl);

        const statusFromFlag = refreshed ? refreshed.is_avail : (uiMatch ? leg.seat_avail_ui.is_avail : null);

        let statusClass = 'unknown';
        if (statusFromFlag === true) statusClass = 'available';
        else if (statusFromFlag === false) statusClass = 'not-available';
        else if (isNotAvail) statusClass = 'not-available';
        else if (isRac || isAvbl) statusClass = 'available';

        return { uniqueKey, displayCls, displayQuota, availInfo, cost, statusClass };
    };

    // Always resolve to a valid class — handles stale state on first render
    const selectedIdx = availableClasses.findIndex(c => compareClasses(c, selectedClass));
    const effectiveClass = selectedIdx !== -1 ? selectedClass : availableClasses[0];
    const effectiveIdx = selectedIdx !== -1 ? selectedIdx : 0;
    const activeData = effectiveClass ? getClassData(effectiveClass, effectiveIdx) : null;
    const activeUniqueKey = effectiveClass ? `${leg.train_num}_${effectiveIdx}` : null;
    const isActiveRefreshing = activeUniqueKey !== null && refreshingIdx === activeUniqueKey;

    return (
        <div className="seat-availability-swiper-container">
            <Swiper
                spaceBetween={12}
                slidesPerView="auto"
                freeMode={true}
                navigation={true}
                observer={true}
                observeParents={true}
                grabCursor={true}
                modules={[FreeMode, Navigation, Keyboard, Mousewheel]}
                keyboard={{ enabled: true }}
                mousewheel={{ forceToAxis: true }}
                className="seat-availability-swiper"
            >
                {availableClasses.map((cls, idx) => {
                    const data = getClassData(cls, idx);
                    const isSelected = compareClasses(effectiveClass, cls);

                    return (
                        <SwiperSlide key={idx} style={{ width: 'auto' }}>
                            <div
                                className={`seat-class-box ${data.statusClass} ${isSelected ? 'selected-seat-class' : ''}`}
                                onClick={() => setSelectedClass(cls)}
                            >
                                <div className="seat-card-grid">
                                    <div className="seat-grid-top-left">{data.displayCls}</div>
                                    <div className="seat-grid-top-right">({data.displayQuota})</div>
                                    <div className="seat-grid-bottom-left">
                                        <span className={`seat-status-text-styled ${data.statusClass}`}>
                                            {data.availInfo || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="seat-grid-bottom-right">
                                        {data.cost ? `₹${data.cost}` : '--'}
                                    </div>
                                </div>

                                <div
                                    className={`seat-refresh-layer ${refreshingIdx === data.uniqueKey ? 'refreshing' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedClass(cls);
                                        handleRefresh(leg, cls, idx);
                                    }}
                                >
                                    {refreshingIdx === data.uniqueKey ? (
                                        <svg width="22" height="22" viewBox="0 0 50 50" className="seat-loading-spinner">
                                            <circle cx="25" cy="25" r="20" stroke="#888" strokeWidth="4" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round">
                                                <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                            </circle>
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" className="seat-refresh-svg">
                                            <path d="M4 4v5h5" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M2.5 10a7.5 7.5 0 1 1 2.2 5.3" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
            <div className="seat-availability-detail-text">
                {isActiveRefreshing ? (
                    <div className="seat-detail-skeleton">
                        <div className="skeleton-bar" style={{ width: 90 }} />
                        <div className="skeleton-bar" style={{ width: 50 }} />
                    </div>
                ) : activeData ? (
                    <div></div>
                ) : <div></div>}
                {actionButton && (
                    <div className="seat-detail-right">
                        {actionButton}
                    </div>
                )}
            </div>
        </div>
    );
};

const formatDuration = (mins) => {
    if (!mins || isNaN(mins)) return '';
    const m = parseInt(mins);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    const remM = m % 60;
    return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
};

const calculateTotalDuration = (segments) => {
    if (!segments || segments.length === 0) return null;
    try {
        const firstNode = segments[0].nodes[0];
        const lastSeg = segments[segments.length - 1];
        const lastNode = lastSeg.nodes[lastSeg.nodes.length - 1];
        const start = new Date(firstNode.dep || firstNode.origin_dep_dt || firstNode.origin_dep);
        const end = new Date(lastNode.arr || lastNode.dest_arr_dt || lastNode.dest_arr);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
        const diffMs = end - start;
        const totalMins = Math.floor(diffMs / (1000 * 60));
        if (totalMins <= 0) return null;
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return `${h}h ${m}m`;
    } catch (e) { return null; }
};

const genKey = (lat, lng) => `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;

const MODE_CONFIG = {
    flight: { color: '#6366F1', emoji: '✈️', label: 'Flight' },
    train: { color: '#ff5b15', emoji: '🚂', label: 'Train' },
    bus: { color: '#16A34A', emoji: '🚌', label: 'Bus' },
    cab: { color: '#10B981', emoji: '🚕', label: 'Cab' },
    transfer: { color: '#10B981', emoji: '🚕', label: 'Cab' },
    unknown: { color: '#64748B', emoji: '📍', label: 'Transport' }
};

const getModeConfig = (mode) => MODE_CONFIG[mode] || MODE_CONFIG.unknown;

const getRawSegmentMode = (seg) => {
    if (!seg) return 'unknown';
    if (seg.train_num || seg.train_num_in_db) return 'train';
    if (seg.flight_num) return 'flight';
    if (seg.inventory_type === 7 || seg.bus_type || seg.boarding_points || seg.dropping_points) return 'bus';
    return 'cab';
};

const createCustomIcon = (type) => {
    if (type === 'start') {
        const iconHtml = renderToStaticMarkup(<div style={{ width: '14px', height: '14px', backgroundColor: 'white', borderRadius: '50%', border: '3.5px solid #1e293b', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />);
        return L.divIcon({ html: iconHtml, className: 'custom-map-icon-start', iconSize: [18, 18], iconAnchor: [9, 9] });
    }
    if (type === 'end') {
        const iconHtml = renderToStaticMarkup(<div style={{ color: '#EA4335', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }}><FaMapMarkerAlt size={32} /></div>);
        return L.divIcon({ html: iconHtml, className: 'custom-map-icon-end', iconSize: [32, 32], iconAnchor: [16, 32] });
    }

    let icon, color = '#475569', size = '12px';
    const isStopover = type === 'stopover';

    if (type === 'flight') { icon = <FaPlane />; color = '#6366F1'; }
    else if (type === 'train') { icon = <FaTrain />; color = '#ff5b15'; }
    else if (type === 'bus') { icon = <FaBus />; color = '#16A34A'; }
    else if (type === 'cab' || type === 'transfer') { icon = <FaTaxi />; color = '#10B981'; }
    else if (type === 'stopover') { icon = <FaExchangeAlt />; color = '#8b5cf6'; size = '16px'; }
    else { icon = <FaMapMarkerAlt />; }

    const containerSize = isStopover ? '30px' : '20px';
    const iconHtml = renderToStaticMarkup(
        <div style={{ color: color, fontSize: size, backgroundColor: 'white', borderRadius: '50%', width: containerSize, height: containerSize, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.25)', border: `${isStopover ? '2.5px' : '1.5px'} solid ${color}` }}>
            {icon}
        </div>
    );
    const LSize = isStopover ? 32 : 22;
    return L.divIcon({ html: iconHtml, className: `custom-map-icon ${isStopover ? 'stopover-glow' : ''}`, iconSize: [LSize, LSize], iconAnchor: [LSize / 2, LSize / 2] });
};

const MapBoundsHandler = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        if (points && points.length > 0 && map) {
            try {
                const bounds = L.latLngBounds(points);
                map.fitBounds(bounds, { padding: [80, 80], maxZoom: 12 });
            } catch (err) { console.warn("FitBounds failed:", err); }
        }
    }, [points, map]);
    return null;
};

// ---------------------------------------------------------------------------
// Shared-route rdata helpers.
//
// New share links use DEFLATE compression + base64url. This keeps the redirected
// /shared-route URL much smaller than the old raw-JSON base64 format and avoids
// CloudFront's ~8 KB request-target limit for normal routes.
//
// The decoder intentionally keeps a legacy fallback so existing share links
// containing the old uncompressed base64 rdata continue to work.
// ---------------------------------------------------------------------------
const RDATA_STASH_PREFIX = 'rc_shared_rdata:';

const base64UrlToUint8 = (value) => {
    const base64 = String(value || '')
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
};

const decodeRdata = (value) => {
    if (!value) return null;

    // New format: DEFLATE-compressed UTF-8 JSON encoded as base64url.
    try {
        return pako.inflate(base64UrlToUint8(value), { to: 'string' });
    } catch (compressedError) {
        // Legacy format: raw UTF-8 JSON encoded as standard base64.
        try {
            return decodeURIComponent(escape(atob(value)));
        } catch (legacyError) {
            throw new Error('Unable to decode shared route data.');
        }
    }
};

const rdataKey = (str) => {
    // Small stable hash (djb2) so the same route always maps to the same key
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
};

const readStashedRdata = (key) => {
    if (!key) return null;
    try { return sessionStorage.getItem(RDATA_STASH_PREFIX + key); } catch (e) { return null; }
};

const stashRdata = (key, value) => {
    try { sessionStorage.setItem(RDATA_STASH_PREFIX + key, value); return true; } catch (e) { return false; }
};

const SharedMapPage = () => {
    const { shortId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [refreshingIdx, setRefreshingIdx] = useState(null);
    const [refreshedInfo, setRefreshedInfo] = useState({});
    const [isBookingLoading, setIsBookingLoading] = useState(false);
    const { openLoginModal } = useAuth();
    const [mapData, setMapData] = useState(null);
    const [sharedRoute, setSharedRoute] = useState(null);
    const [error, setError] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    // Booking flow states
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [errorModalData, setErrorModalData] = useState(null);

    const from = searchParams.get('from');
    const to = searchParams.get('to');
    const date = searchParams.get('date');
    const urlRdata = searchParams.get('rdata');
    const sid = searchParams.get('sid');   // short code, if the /r/ resolver passed it
    const rk = searchParams.get('rk');     // stash key, used when there is no short code
    const location = useLocation();

    // Same string whether it came from the URL or the stash, so stripping the URL
    // below does not re-trigger the fetch effect.
    const rdata = useMemo(
        () => urlRdata || readStashedRdata(sid) || readStashedRdata(rk),
        [urlRdata, sid, rk]
    );

    // Strip rdata from the URL once we have it safely stashed
    useEffect(() => {
        if (!urlRdata) return;
        const key = sid || rdataKey(urlRdata);
        if (!stashRdata(key, urlRdata)) return; // storage blocked: keep the long URL rather than lose the route
        const params = new URLSearchParams(searchParams);
        params.delete('rdata');
        if (!sid) params.set('rk', key);
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlRdata]);

    // Short URL but nothing stashed (opened on another device / new tab):
    // re-resolve through the short link, which brings rdata back.
    const needsReResolve = !urlRdata && !rdata && !!sid;
    useEffect(() => {
        if (needsReResolve) navigate(`/r/${encodeURIComponent(sid)}`, { replace: true });
    }, [needsReResolve, sid, navigate]);

    const searchMetadata = useMemo(() => ({
        user_src_name: from || sharedRoute?.user_src_name || sharedRoute?.nodes?.[0]?.segment?.origin_name,
        user_dest_name: to || sharedRoute?.user_dest_name || sharedRoute?.nodes?.[sharedRoute?.nodes?.length - 1]?.segment?.dest_name,
        user_src_ll: sharedRoute?.user_src_ll || null,
        user_dest_ll: sharedRoute?.user_dest_ll || null,
        date: date || sharedRoute?.date,
        fallback_url: sharedRoute?.fallback_url || ""
    }), [from, to, date, sharedRoute]);
    console.log(sharedRoute)
    const getOverallSeatStatus = () => {
        if (sharedRoute?.seatStatus) return sharedRoute.seatStatus;
        if (!sharedRoute?.nodes) return "Seats available";

        const segments = sharedRoute.nodes.map(n => n.segment).filter(Boolean);
        let allAvailable = true;
        let anyAvailable = false;

        segments.forEach(seg => {
            let isSegAvailable = false;
            if (seg.train_num) {
                // Train specific logic
                const classes = seg.seat_classes || [];
                isSegAvailable = classes.some(cls => {
                    const uiMatch = seg.seat_avail_ui && compareClasses(seg.seat_avail_ui.seat_class, cls);
                    const availInfo = uiMatch ? seg.seat_avail_ui.avl : (seg.seat_avail?.[cls] || null);
                    const statusFromFlag = uiMatch ? seg.seat_avail_ui.is_avail : null;
                    if (statusFromFlag === true) return true;
                    if (statusFromFlag === false) return false;
                    const avail = (availInfo || "").toUpperCase();
                    const isAvbl = (avail.includes("AVAILABLE") || avail.includes("AVL") || avail.includes("AVBL")) && !avail.includes("NOT");
                    const isRac = avail.includes("RAC");
                    return isAvbl || isRac;
                });
            } else {
                // Flights, Cabs, Transfers are considered available
                isSegAvailable = true;
            }

            if (!isSegAvailable) allAvailable = false;
            if (isSegAvailable) anyAvailable = true;
        });

        if (allAvailable) return "Seats available";
        if (anyAvailable) return "Seats partially available";
        return "Seats not available";
    };

    const overallStatus = getOverallSeatStatus();
    const statusClass = overallStatus.toLowerCase().replace(/ /g, '-');

    useEffect(() => {
        const fetchSharedData = async () => {
            // Allow from/to to be missing initially if rdata is present
            if (needsReResolve) return; // redirecting to /r/:sid
            if (!rdata && rk) {
                // Stash was for another tab/device and there is no short code to re-resolve
                setLoading(false);
                setError("This shared route link has expired. Please open the original share link again.");
                return;
            }
            if (!from || !to) {
                if (!rdata) {
                    setLoading(false);
                    setError("Missing location information.");
                    return;
                }
            }
            setLoading(true);
            try {
                let routes = [];
                let decodedRoute = null;
                if (rdata) {
                    try {
                        const decodedStr = decodeRdata(rdata);
                        decodedRoute = JSON.parse(decodedStr);
                        setSharedRoute(decodedRoute);
                        routes = [decodedRoute];
                    } catch (e) { console.error("Decoding failed:", e); }
                }

                // Fallback for missing from/to from decodedRoute if URL params are missing
                const effectiveFrom = from || decodedRoute?.nodes?.[0]?.segment?.origin_name || decodedRoute?.departure?.location;
                const effectiveTo = to || decodedRoute?.nodes?.[decodedRoute?.nodes?.length - 1]?.segment?.dest_name || decodedRoute?.arrival?.location;

                if (!effectiveFrom || !effectiveTo) {
                    throw new Error("Missing location information in shared route.");
                }

                // Construct metadata locally to avoid waiting for state update
                const effectiveMetadata = {
                    user_src_name: effectiveFrom,
                    user_dest_name: effectiveTo,
                    user_src_ll: decodedRoute?.user_src_ll || null,
                    user_dest_ll: decodedRoute?.user_dest_ll || null,
                    date: date || decodedRoute?.date || new Date().toISOString().split('T')[0],
                    fallback_url: decodedRoute?.fallback_url || ""
                };

                const payload = {
                    route_obj: {
                        status: 200,
                        data: {
                            routes: routes,
                            from: effectiveFrom,
                            to: effectiveTo,
                            date: effectiveMetadata.date,
                            ...effectiveMetadata
                        }
                    }
                };
                const response = await fetch('https://hh183pxn0f.execute-api.ap-south-1.amazonaws.com/development/maps', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
                });
                const rawData = await response.json();
                let data = rawData;
                if (rawData.body && typeof rawData.body === 'string') { try { data = JSON.parse(rawData.body); } catch (e) { } }

                if (Array.isArray(data) && data.length > 0) setMapData(data[0]);
                else if (data && data.segments) setMapData(data);
                else if (data && data.data && (data.data.segments || Array.isArray(data.data))) setMapData(Array.isArray(data.data) ? data.data[0] : data.data);
                else if (!rdata) throw new Error(data.error || data.message || 'No map segments found for this route');
                else setMapData({ segments: [] });
            } catch (err) {
                console.error('SharedMapPage API Error:', err);
                if (!rdata) setError(err.message);
            } finally { setLoading(false); }
        };
        fetchSharedData();
        // rk is left out on purpose: it appears when we strip rdata, and refetching then is wasted work
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, date, rdata, needsReResolve]);

    const allPoints = useMemo(() => {
        const points = [];
        mapData?.segments?.forEach(seg => { seg.nodes?.forEach(node => { if (node.lat && node.lng && !(node.lat === 0 && node.lng === 0)) points.push([node.lat, node.lng]); }); });
        return points;
    }, [mapData]);

    const totalDuration = useMemo(() => {
        if (sharedRoute) {
            const apiVal = sharedRoute.total_duration || sharedRoute.duration_text;
            if (apiVal && apiVal !== 'N/A' && apiVal !== '---') return apiVal;
            try {
                const nodes = sharedRoute.nodes;
                const start = new Date(nodes[0].segment.origin_dep_dt);
                const end = new Date(nodes[nodes.length - 1].segment.dest_arr_dt);
                const diff = (end - start) / (1000 * 60);
                if (diff > 0) return `${Math.floor(diff / 60)}h ${Math.floor(diff % 60)}m`;
            } catch (e) { }
        }
        if (mapData) return calculateTotalDuration(mapData.segments) || 'N/A';
        return '---';
    }, [mapData, sharedRoute]);

    // Cab selections (origin/dest/layover cabs from the route payload)
    const [cabSel, setCabSel] = useState({});
    useEffect(() => {
        if (!sharedRoute?.nodes?.length) return;
        const first = sharedRoute.nodes[0]?.segment;
        const last = sharedRoute.nodes[sharedRoute.nodes.length - 1]?.segment;
        const init = {};
        if (first?.origin_cab) init.origin = defaultCabCategory(first.origin_cab);
        if (last?.dest_cab) init.dest = defaultCabCategory(last.dest_cab);
        (sharedRoute.stops_data || []).forEach((st, i) => {
            if (st?.layover_cab) init[`l${i}`] = defaultCabCategory(st.layover_cab);
        });
        setCabSel(init);
    }, [sharedRoute]);

    const cabTotal = useMemo(() => {
        if (!sharedRoute?.nodes?.length) return 0;
        const first = sharedRoute.nodes[0]?.segment;
        const last = sharedRoute.nodes[sharedRoute.nodes.length - 1]?.segment;
        let t = 0;
        if (first?.origin_cab) t += selectedCabPrice(first.origin_cab, cabSel.origin);
        if (last?.dest_cab) t += selectedCabPrice(last.dest_cab, cabSel.dest);
        (sharedRoute.stops_data || []).forEach((st, i) => {
            if (st?.layover_cab) t += selectedCabPrice(st.layover_cab, cabSel[`l${i}`]);
        });
        return t;
    }, [sharedRoute, cabSel]);

    const totalFare = useMemo(() => {
        if (sharedRoute) {
            try {
                const parsePrice = (p) => {
                    if (typeof p === 'number') return p;
                    if (typeof p !== 'string') return 0;
                    return parseFloat(p.replace(/[^0-9.]/g, '')) || 0;
                };

                let sum = 0;
                let hasCalculatedSum = false;

                sharedRoute.nodes.forEach(node => {
                    const s = node.segment;
                    if (!s) return;
                    // Check multiple possible fare fields including UI-specific and generic ones
                    const f = s.seat_avail_ui?.fare || s.seat_avail_ui?.General?.fare || s.seat_avail?.fare || s.fare || s.price || s.net_fare;
                    const val = parsePrice(f);
                    if (val > 0) {
                        sum += val;
                        hasCalculatedSum = true;
                    }
                });

                if (hasCalculatedSum && sum > 0) return sum.toLocaleString('en-IN');

                // Fallback to pre-calculated total from API if manual sum fails or is empty
                const apiFare = sharedRoute.total_fare || sharedRoute.totalFare || sharedRoute.fare;
                if (apiFare && apiFare !== '---') {
                    const fallbackVal = parsePrice(apiFare);
                    return fallbackVal > 0 ? fallbackVal.toLocaleString('en-IN') : apiFare;
                }
            } catch (e) {
                console.error("Error calculating total fare:", e);
            }
        }
        return '---';
    }, [sharedRoute]);

    const handleRefresh = async (leg, cls, idx) => {
        const uniqueKey = `${leg.train_num}_${idx}`;
        setRefreshingIdx(uniqueKey);
        try {
            const parseDate = (dStr) => {
                if (!dStr) return null;
                const d = new Date(dStr);
                return (!isNaN(d.getTime())) ? d : null;
            };

            let dateObj = parseDate(leg.origin_dep_dt);
            const formattedDate = dateObj
                ? `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`
                : '';

            if (!formattedDate) {
                setRefreshingIdx(null);
                return;
            }

            const params = new URLSearchParams({
                train_num: leg.train_num || '',
                src: leg.origin_code || '',
                dest: leg.dest_code || '',
                date: formattedDate,
                pref_train_class: cls,
                quota: (leg.category && !leg.category.includes('Reserved')) ? leg.category : 'GN',
                api_key: 'web_dev_1',
                get_avail_only: '1'
            }).toString();

            const url = `https://hh183pxn0f.execute-api.ap-south-1.amazonaws.com/development/search?${params}`;
            const res = await fetch(url, { method: "GET" });
            const data = await res.json();

            if (data.status === 200 && data.data?.seat_avail_ui) {
                setRefreshedInfo(prev => ({
                    ...prev,
                    [uniqueKey]: {
                        avl: data.data.seat_avail_ui.avl,
                        fare: data.data.seat_avail_ui.fare,
                        is_avail: data.data.seat_avail_ui.is_avail,
                        quota: data.data.seat_avail_ui.quota
                    }
                }));
            }
        } catch (e) {
            console.error('Refresh error:', e);
        } finally {
            setTimeout(() => setRefreshingIdx(null), 500);
        }
    };

    const firstWord = (value) => {
        if (value === null || value === undefined) return '';
        const text = String(value).trim();
        return text ? text.split(/\s+/)[0] : '';
    };

    const getBusStationDisplay = (seg, side) => {
        if (!seg) return '';
        const direct = side === 'origin' ? seg.origin_name : seg.dest_name;
        const directFirst = firstWord(direct);
        if (directFirst) return directFirst;
        const nested = side === 'origin' ? seg.origin : seg.destination;
        if (nested && typeof nested === 'object') {
            return firstWord(nested.name || nested.city_name || nested.cityName || nested.display_name || nested.displayName || nested.label);
        }
        return '';
    };

    const handleBookFlight = async (seg) => {
        const auth = getAuth();
        const user = auth.currentUser;

        // Save context for redirect back
        localStorage.setItem("booking_source", "shared_map");
        localStorage.setItem("booking_search_query", window.location.search);

        const trace_id = seg.trace_id || sharedRoute?.trace_id;
        const result_idx = seg.result_idx;

        setShowLoadingModal(true);
        setErrorModalData(null);

        try {
            let userToken = null;
            if (user) userToken = await user.getIdToken();

            // Verify fare quote
            const fareQuoteResponse = await fetchFareQuote(trace_id, result_idx, userToken);

            const params = new URLSearchParams();
            if (trace_id) params.append("trace_id", trace_id);
            if (result_idx !== undefined) params.append("result_idx", result_idx);
            if (seg.is_lcc !== undefined) params.append("is_lcc", seg.is_lcc);
            if (seg.valid_till_ts) params.append("valid_till_ts", seg.valid_till_ts);

            const url = `/book-flight?${params.toString()}`;

            navigate(url, {
                state: {
                    preFetchedQuote: fareQuoteResponse,
                    trace_id,
                    result_idx,
                    is_lcc: seg.is_lcc,
                    valid_till_ts: seg.valid_till_ts,
                    baggage: seg.baggage,
                    userToken,
                    source: "shared_map",
                    searchQuery: window.location.search,
                    flightDetails: {
                        departure: { name: seg.origin_name, code: seg.origin_code, time: formatTime(seg.origin_dep_dt) },
                        arrival: { name: seg.dest_name, code: seg.dest_code, time: formatTime(seg.dest_arr_dt) },
                        duration: formatDuration(seg.commute_time_mins),
                        stops: 0,
                        price: seg.price,
                        currency: '₹',
                        tag: seg.carrier || '',
                        modes: ['flight'],
                    },
                },
            });
        } catch (error) {
            console.error("Booking failed:", error);
            setShowLoadingModal(false);

            const isAuthError =
                error.status === 401 ||
                error.response?.status === 401 ||
                error.message?.toLowerCase().includes("unauthorized") ||
                error.message?.toLowerCase().includes("login required");

            if (isAuthError) {
                openLoginModal(() => handleBookFlight(seg));
            } else {
                setErrorModalData({
                    message: error.message || "Failed to verify fare details. This search session may have expired.",
                    title: "Booking Session Error",
                    onRetry: () => handleBookFlight(seg)
                });
            }
        } finally {
            setShowLoadingModal(false);
        }
    };

    const mapElements = useMemo(() => {
        if (!mapData?.segments) return null;
        const validSegments = mapData.segments.filter(seg => { const lastNode = seg.nodes[seg.nodes.length - 1]; return !(lastNode?.lat === 0 && lastNode?.lng === 0); });
        if (validSegments.length === 0) return null;

        const markersMap = new Map();
        const startKey = genKey(validSegments[0].nodes[0].lat, validSegments[0].nodes[0].lng);
        const lastSegNodes = validSegments[validSegments.length - 1].nodes;
        const endKey = genKey(lastSegNodes[lastSegNodes.length - 1].lat, lastSegNodes[lastSegNodes.length - 1].lng);

        validSegments.forEach((seg, sIdx) => {
            seg.nodes.forEach((n, nIdx) => {
                if (!n || isNaN(n.lat) || isNaN(n.lng) || (n.lat === 0 && n.lng === 0)) return;
                const key = genKey(n.lat, n.lng);

                // Skip start/end points as they are handled separately with special markers
                if (key === startKey || key === endKey) return;

                // Check if this node is a Junction or a Stopover
                const isJunction = /\bJN\b|\bjn\b|\bJunction\b|\bjunction\b/i.test(n.name || "");
                const isStopoverPoint = nIdx === 0 || nIdx === seg.nodes.length - 1;

                // Only add significant markers (Junctions or Transfer points)
                if (isJunction || isStopoverPoint) {
                    if (!markersMap.has(key)) {
                        markersMap.set(key, {
                            pos: [n.lat, n.lng],
                            node: { ...n },
                            type: seg.type,
                            isStopover: false
                        });
                    }
                    const current = markersMap.get(key);

                    // Helper to find time in original routeData if map response is missing it
                    const findSocketTime = (name, type) => {
                        if (!sharedRoute?.nodes) return null;
                        const norm = (s) => s?.toLowerCase().replace(/[^a-z0-9]/g, '').trim();
                        const targetNorm = norm(name);

                        for (const rn of sharedRoute.nodes) {
                            const s = rn.segment;
                            if (type === 'arr' && (norm(s?.dest_name) === targetNorm || norm(s?.dest_code) === targetNorm))
                                return s.dest_arr_dt || s.dest_arr;
                            if (type === 'dep' && (norm(s?.origin_name) === targetNorm || norm(s?.origin_code) === targetNorm))
                                return s.origin_dep_dt || s.origin_dep;
                        }
                        return null;
                    };

                    if (nIdx === seg.nodes.length - 1 && sIdx < validSegments.length - 1) {
                        current.node.arr = n.arr || seg.dest_arr_dt || seg.dest_arr || findSocketTime(n.name, 'arr');
                        current.inbound = seg;
                        current.isStopover = true;
                        current.type = 'stopover';
                    }
                    if (nIdx === 0 && sIdx > 0) {
                        current.node.dep = n.dep || seg.origin_dep_dt || seg.origin_dep || findSocketTime(n.name, 'dep');
                        current.outbound = seg;
                        current.isStopover = true;
                        current.type = 'stopover';
                    }
                }
            });
        });

        return (
            <>
                {validSegments.map((seg, sIdx) => {
                    const positions = seg.nodes
                        .filter(n => n && typeof n.lat === 'number' && typeof n.lng === 'number' && !isNaN(n.lat) && !isNaN(n.lng) && n.lat !== 0)
                        .map(n => [n.lat, n.lng]);

                    if (positions.length < 2) return null;

                    const color = getModeConfig(seg.type).color;
                    return <Polyline key={`poly-${sIdx}`} positions={positions} pathOptions={{ color: color, weight: 4, opacity: 0.8 }} />;
                })}
                {validSegments.map((seg, sIdx) => {
                    const vNodes = seg.nodes.filter(n => n && typeof n.lat === 'number' && typeof n.lng === 'number' && !isNaN(n.lat) && !isNaN(n.lng) && n.lat !== 0);
                    if (vNodes.length < 2) return null;

                    let lp;
                    if (vNodes.length === 2) {
                        lp = [(vNodes[0].lat + vNodes[1].lat) / 2, (vNodes[0].lng + vNodes[1].lng) / 2];
                    } else {
                        const midIdx = Math.floor(vNodes.length / 2);
                        lp = [vNodes[midIdx].lat, vNodes[midIdx].lng];
                    }

                    if (!lp || isNaN(lp[0]) || isNaN(lp[1])) return null;

                    const modeConfig = getModeConfig(seg.type);
                    const emoji = modeConfig.emoji;
                    const color = modeConfig.color;
                    const displayType = modeConfig.label;
                    const isBus = seg.type === 'bus';

                    const displayName = isBus
                        ? (seg.meta?.travel_name || seg.meta?.service_name || seg.id || 'Bus')
                        : (
                            seg.id &&
                            !(
                                String(seg.id).toLowerCase() === String(seg.type).toLowerCase() ||
                                String(seg.id).toLowerCase() === 'cab'
                            )
                                ? `#${seg.id}`
                                : displayType
                        );

                    const phtml = renderToStaticMarkup(
                        <span
                            className={`route-pill-label underlined ${seg.type}`}
                            style={{
                                borderColor: color,
                                color: color
                            }}
                        >
                            {emoji} {displayName}
                        </span>
                    );

                    const distKm = seg.meta?.distance_kms || seg.distance_kms;
                    const timeMins =
                        seg.meta?.duration_mins ||
                        seg.duration_mins ||
                        seg.meta?.time_mins ||
                        seg.time_mins;

                    const busType = seg.meta?.bus_type || seg.bus_type;
                    const serviceName = seg.meta?.service_name || seg.service_name;
                    const travelName = seg.meta?.travel_name || seg.travel_name;

                    return (
                        <Marker key={`label-${sIdx}`} position={lp} icon={L.divIcon({ html: phtml, className: `route-pill-icon ${seg.type}`, iconSize: [null, null], iconAnchor: [40, 28] })}>
                            <Tooltip direction="top" offset={[0, -15]} className="route-hover-tooltip">
                                <div className={`modern-map-tooltip ${seg.type}`}>
                                    <div className="tooltip-header" style={{ background: color }}>
                                        <strong>{emoji} {displayName}</strong>
                                    </div>
                                    <div className="tooltip-body">
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                                            {vNodes[0].name || seg.from_code} ➔ {vNodes[vNodes.length - 1].name || seg.to_code}
                                        </div>

                                        {isBus ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#475569' }}>
                                                {busType && <div><strong>Bus:</strong> {busType}</div>}
                                                {serviceName && serviceName !== travelName && <div><strong>Service:</strong> {serviceName}</div>}
                                                {timeMins && <div><strong>Duration:</strong> {formatDuration(timeMins)}</div>}
                                            </div>
                                        ) : (
                                            (distKm || timeMins) && (
                                                <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    {emoji} {distKm ? `${distKm} km` : ''}
                                                    {distKm && timeMins ? ' · ' : ''}
                                                    {timeMins ? formatDuration(timeMins) : ''}
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </Tooltip>
                        </Marker>
                    );
                })}
                {Array.from(markersMap.values()).map((m, i) => (
                    <Marker key={i} position={m.pos} icon={createCustomIcon(m.type)}>
                        <Tooltip direction="top" offset={[0, -5]} opacity={1}>
                            <div className={`modern-map-tooltip ${m.isStopover ? 'stopover' : 'junction'}`}>
                                <div className="tooltip-header">
                                    <strong>{m.node.name}</strong>
                                    {m.isStopover && <span className="stopover-badge">Stopover</span>}
                                </div>
                                <div className="tooltip-body">
                                    <div className="timing-container">
                                        <div className="timing-col">
                                            <span className="label">Arrival {m.inbound?.id && <span className="train-id">(#{m.inbound.id})</span>}</span>
                                            <span className="time">{formatTime(m.node.arr) || '--:--'}</span>
                                        </div>
                                        <div className="v-divider"></div>
                                        <div className="timing-col">
                                            <span className="label">Departure {m.outbound?.id && <span className="train-id">(#{m.outbound.id})</span>}</span>
                                            <span className="time">{formatTime(m.node.dep) || '--:--'}</span>
                                        </div>
                                    </div>
                                    {(() => {
                                        if (!m.isStopover) return null;
                                        const layo = calculateLayover(m.node.arr || '', m.node.dep || '');
                                        if (!layo) return null;
                                        return (
                                            <>
                                                <div className="h-divider"></div>
                                                <div className="gap-row">Gap: {layo}</div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </Tooltip>
                    </Marker>
                ))}
                {(() => {
                    const sN = validSegments[0].nodes[0];
                    const eN = lastSegNodes[lastSegNodes.length - 1];
                    return (
                        <>
                            <Marker position={[sN.lat, sN.lng]} icon={createCustomIcon('start')} zIndexOffset={1000}>
                                <Tooltip direction="top" offset={[0, -5]}>
                                    <div className="modern-map-tooltip junction">
                                        <div className="tooltip-header">
                                            <strong>{sN.name}</strong>
                                        </div>
                                        <div className="tooltip-body">
                                            <div className="simple-time-row">
                                                <span className="label">Departure:</span>
                                                <span className="time">{formatTime(sN.dep || sN.origin_dep || sN.origin_dep_dt) || '--:--'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Tooltip>
                            </Marker>
                            <Marker position={[sN.lat, sN.lng]} icon={L.divIcon({ html: `<div class="station-name-pill source">${sN.name}</div>`, className: 'floating-city-label', iconSize: [null, null], iconAnchor: [0, 0] })} interactive={false} />
                            <Marker position={[eN.lat, eN.lng]} icon={createCustomIcon('end')} zIndexOffset={1000}>
                                <Tooltip direction="top" offset={[0, -5]}>
                                    <div className="modern-map-tooltip junction">
                                        <div className="tooltip-header">
                                            <strong>{eN.name}</strong>
                                        </div>
                                        <div className="tooltip-body">
                                            <div className="simple-time-row">
                                                <span className="label">Arrival:</span>
                                                <span className="time">
                                                    {formatTime(
                                                        eN.arr ||
                                                        eN.dest_arr ||
                                                        eN.dest_arr_dt ||
                                                        (sharedRoute?.nodes?.[sharedRoute.nodes.length - 1]?.segment?.dest_arr_dt) ||
                                                        (sharedRoute?.nodes?.[sharedRoute.nodes.length - 1]?.segment?.dest_arr)
                                                    ) || '--:--'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Tooltip>
                            </Marker>
                            <Marker position={[eN.lat, eN.lng]} icon={L.divIcon({ html: `<div class="station-name-pill dest">${eN.name}</div>`, className: 'floating-city-label', iconSize: [null, null], iconAnchor: [0, 0] })} interactive={false} />
                        </>
                    );
                })()}
                {/* Transfer Polylines */}
                {mapData.stops?.map((stop, stIdx) => (
                    <Polyline
                        key={`stop-${stIdx}`}
                        positions={[[stop.from_node.lat, stop.from_node.lng], [stop.to_node.lat, stop.to_node.lng]]}
                        pathOptions={{ color: '#10B981', weight: 3, opacity: 0.6 }}
                    >
                        <Tooltip permanent direction="top" offset={[0, -10]} className="cab-tooltip">
                            <span>🚕 Cab {stop.commute_dist_kms ? `· ${stop.commute_dist_kms} km` : ''}{stop.commute_time_mins ? ` · ${stop.commute_time_mins} min` : ''}</span>
                        </Tooltip>
                    </Polyline>
                ))}
            </>
        );
    }, [mapData]);

    if (loading) return <div className="shared-map-loading"><div className="loader-box"><div className="spinner"></div><p>Plotting shared journey...</p></div></div>;
    if (error && !sharedRoute) return <div className="shared-map-error"><div className="error-card"><Info size={40} /><h2>Oops!</h2><p>{error}</p><button onClick={() => navigate('/')}>Go to Home</button></div></div>;

    return (
        <div className="shared-page-container">
            <header className="shared-page-header">
                <div className="header-left">
                    <Logo />
                </div>
                <div className="header-center">
                    <div className="route-badge">
                        {(() => {
                            if (!sharedRoute?.nodes || sharedRoute.nodes.length === 0) {
                                return (
                                    <>
                                        <span className="city">{from}</span>
                                        <span className="arrow">→</span>
                                        <span className="city">{to}</span>
                                    </>
                                );
                            }

                            const firstSeg = sharedRoute.nodes[0].segment;
                            const lastSeg = sharedRoute.nodes[sharedRoute.nodes.length - 1].segment;

                            const startText = sharedRoute.user_src_name || (firstSeg?.origin_name ?
                                `${firstSeg.origin_name}${(firstSeg.origin_code_iata || firstSeg.origin_code || firstSeg.seat_avail_ui?.src) ? ` (${firstSeg.origin_code_iata || firstSeg.origin_code || firstSeg.seat_avail_ui?.src})` : ''}` :
                                from);

                            const endText = sharedRoute.user_dest_name || (lastSeg?.dest_name ?
                                `${lastSeg.dest_name}${(lastSeg.dest_code_iata || lastSeg.dest_code || lastSeg.seat_avail_ui?.dest) ? ` (${lastSeg.dest_code_iata || lastSeg.dest_code || lastSeg.seat_avail_ui?.dest})` : ''}` :
                                to);

                            return (
                                <>
                                    <span className="city">{startText}</span>
                                    <span className="arrow">→</span>
                                    <span className="city">{endText}</span>
                                </>
                            );
                        })()}
                    </div>
                </div>
                <div className="header-right"></div>
            </header>

            <div className="shared-main-layout">
                <aside className="shared-sidebar">
                    <div className="journey-header-pro">
                        <div className="header-actions-pro" style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                            {sharedRoute?.fallback_url && (
                                <button
                                    className="back-btn-pro"
                                    onClick={() => window.location.href = sharedRoute.fallback_url}
                                    title="Back to Search Results"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="19" y1="12" x2="5" y2="12"></line>
                                        <polyline points="12 19 5 12 12 5"></polyline>
                                    </svg>
                                    <span>Back to results</span>
                                </button>
                            )}

                        </div>
                    </div>
                    <div className="sidebar-top-pro">
                        <div className="top-status-row">
                            <div className="duration-summary">
                                <span className="label">Total Duration</span>
                                <span className="amount">{totalDuration}</span>
                            </div>
                            <div className="price-summary">
                                <span className="label">Total Fare</span>
                                <span className="amount">₹{totalFare}</span>
                            </div>
                            <div className={`seats-badge ${statusClass}`}>
                                <Info size={16} />
                                <span>{overallStatus}</span>
                            </div>
                        </div>
                    </div>

                    <div className="timeline-scroll-pro">
                        <div className="timeline-container-pro">
                            {(sharedRoute?.nodes || mapData?.segments)?.map((item, i) => {
                                const nodes = sharedRoute?.nodes || mapData?.segments;
                                const isLast = i === nodes.length - 1;

                                if (sharedRoute?.nodes) {
                                    const node = item;
                                    const seg = node.segment;
                                    const mode = getRawSegmentMode(seg);
                                    const time = seg.meta?.duration_mins || seg.duration_mins || seg.meta?.time_mins || seg.time_mins;

                                    return (
                                        <React.Fragment key={i}>
                                            {i === 0 && seg?.origin_cab && (
                                                <div className="timeline-segment-pro connection">
                                                    <div className="sidebar-visual-pro">
                                                        <div className="node-icon-box-pro small">
                                                            <img src="/icon3.svg" alt="Commute" />
                                                        </div>
                                                        <div className="vertical-line-pro"></div>
                                                    </div>
                                                    <div className="leg-details-container-pro">
                                                        <div className="connection-card-pro commute">
                                                            <div className="connection-header-pro">
                                                                <span className="connection-title-pro">{seg.origin_comment_web_title || 'Cab to pickup'}</span>
                                                                <span className="cab-selected-price">₹{selectedCabPrice(seg.origin_cab, cabSel.origin).toLocaleString('en-IN')}</span>
                                                                <span className="connection-tag-pro">Commute</span>
                                                            </div>
                                                            <div className="connection-body-pro">
                                                                {seg.origin_comment_web && <p className="commute-description-pro">{seg.origin_comment_web}</p>}
                                                                <CabOptions
                                                                    cab={seg.origin_cab}
                                                                    selected={cabSel.origin}
                                                                    onSelect={(c) => setCabSel(prev => ({ ...prev, origin: c }))}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="timeline-segment-pro">
                                                <div className="sidebar-visual-pro">
                                                    <div className="node-icon-box-pro">
                                                        {mode === 'flight' && <img src="/icon4.svg" alt="Flight" />}
                                                        {mode === 'train' && <img src="/icon1.svg" alt="Train" />}
                                                        {String(mode).toLowerCase() === 'bus' && <FaBus size={24} color="#16A34A" />}
                                                        {mode === 'cab' && <img src="/icon3.svg" alt="Commute" />}
                                                    </div>
                                                    {(!isLast || sharedRoute.stops_data?.[i] || (isLast && (sharedRoute.commute_dist || sharedRoute.commute_time_mins || item.segment?.dest_comment_web_title))) && <div className="vertical-line-pro"></div>}
                                                </div>
                                                <div className="leg-details-container-pro">
                                                    <div className={`expanded-leg-card-pro ${mode}-type`}>
                                                        <div className="leg-upper-pro">
                                                            <div className="leg-title-info-pro">
                                                                <span className="leg-title-pro">
                                                                    {mode === 'bus'
                                                                        ? `${seg.travel_name || seg.service_name || 'Bus'}${seg.bus_type ? ` · ${seg.bus_type}` : ''}`
                                                                        : (seg.flight_num || seg.train_num
                                                                            ? `${seg.flight_num || seg.train_num}, ${seg.carrier || seg.train_name || ''}`
                                                                            : (mode === 'cab' ? 'Private Cab' : 'Transport'))}
                                                                </span>
                                                            </div>
                                                            <span className="leg-type-tag-pro">{mode.charAt(0).toUpperCase() + mode.slice(1)}</span>
                                                        </div>

                                                        <div className="leg-middle-pro">
                                                            <div className="leg-stations-pro">
                                                                <div className="leg-station-pro">
                                                                    <span className="s-name-pro">{seg.origin_name}</span>
                                                                    <span className="s-main-pro">{String(mode).toLowerCase() === 'bus' ? (getBusStationDisplay(seg, 'origin') || '') : (seg.origin_code_iata || seg.origin_code || seg.seat_avail_ui?.src || '')}, {formatTime(seg.origin_dep_dt)}</span>
                                                                    <span className="s-date-pro">{seg.origin_dep_dt ? new Date(seg.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>
                                                                </div>
                                                                <div className="leg-visual-pro">
                                                                    <span className="d-text-pro">{formatDuration(time)}</span>
                                                                    <div className="d-line-pro"></div>
                                                                </div>
                                                                <div className="leg-station-pro right">
                                                                    <span className="s-name-pro">{seg.dest_name}</span>
                                                                    <span className="s-main-pro">{String(mode).toLowerCase() === 'bus' ? (getBusStationDisplay(seg, 'destination') || '') : (seg.dest_code_iata || seg.dest_code || seg.seat_avail_ui?.dest || '')}, {formatTime(seg.dest_arr_dt)}</span>
                                                                    <span className="s-date-pro">{seg.dest_arr_dt ? new Date(seg.dest_arr_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>
                                                                </div>
                                                            </div>
                                                            {mode === 'train' && (
                                                                <SeatAvailabilityGrid
                                                                    leg={seg}
                                                                    refreshedInfo={refreshedInfo}
                                                                    refreshingIdx={refreshingIdx}
                                                                    handleRefresh={handleRefresh}
                                                                    actionButton={
                                                                        <button
                                                                            className={`book-leg-btn-pro ${mode}`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (seg.booking_link) window.open(seg.booking_link, '_blank');
                                                                            }}
                                                                        >
                                                                            Book on IRCTC
                                                                        </button>
                                                                    }
                                                                />
                                                            )}
                                                            {mode !== 'train' && (
                                                                <div className="leg-action-row-pro" style={(mode === 'flight' || mode === 'bus') ? { justifyContent: 'space-between', alignItems: 'center' } : {}}>
                                                                    {(mode === 'flight' || mode === 'bus') && (
                                                                        <div className="flight-price-pro" style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginLeft: '8px' }}>
                                                                            {(() => {
                                                                                const f = seg.seat_avail_ui?.fare
                                                                                    || seg.seat_avail_ui?.General?.fare
                                                                                    || seg.seat_avail?.fare
                                                                                    || seg.fare || seg.price || seg.net_fare;
                                                                                if (!f || f === '--') return '';
                                                                                const val = typeof f === 'number' ? f : parseFloat(String(f).replace(/[^0-9.]/g, ''));
                                                                                return val > 0 ? `₹${val.toLocaleString('en-IN')}` : '';
                                                                            })()}
                                                                        </div>
                                                                    )}
                                                                    <div className="leg-button-wrapper-pro">
                                                                        <button
                                                                            className={`book-leg-btn-pro ${mode}`}
                                                                            disabled={isBookingLoading && mode === 'flight'}
                                                                            onClick={() => {
                                                                                if (mode === 'flight') {
                                                                                    handleBookFlight(seg);
                                                                                } else if (mode === 'bus') {
                                                                                    navigate('/book-bus', {
                                                                                        state: {
                                                                                            busInfo: { segment: seg },
                                                                                            searchParams: {
                                                                                                from: seg.origin_name,
                                                                                                to:   seg.dest_name,
                                                                                                date: seg.origin_dep_dt?.split('T')[0],
                                                                                                adults: 1,
                                                                                            }
                                                                                        }
                                                                                    });
                                                                                } else if (seg.booking_link) {
                                                                                    window.open(seg.booking_link, '_blank');
                                                                                }
                                                                            }}
                                                                        >
                                                                            {mode === 'train' ? 'Book on IRCTC'
                                                                                : mode === 'bus' ? `Book ${seg.origin_name} → ${seg.dest_name} Bus`
                                                                                : `Book ${mode.charAt(0).toUpperCase() + mode.slice(1)}`}
                                                                        </button>
                                                                        {(mode === 'flight' || mode === 'bus') && sharedRoute?.flat_off_flight_promo_desc && (
                                                                            <span className="flight-deal-badge">
                                                                                <span className="flight-deal-dot"></span>
                                                                                {sharedRoute.flat_off_flight_promo_desc}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="leg-footer-msg-pro">
                                                            <p>
                                                                {mode === 'bus'
                                                                    ? <>Travel by <strong>{seg.travel_name || seg.service_name || 'bus'}</strong>{seg.bus_type ? <> ({seg.bus_type})</> : ''} from <strong>{seg.origin_name}</strong> to <strong>{seg.dest_name}</strong> on {seg.origin_dep_dt ? new Date(seg.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}.</>
                                                                    : <>Book {mode} {seg.carrier || seg.train_name} {seg.flight_num || seg.train_num} from {seg.origin_name} to {seg.dest_name} on {seg.origin_dep_dt ? new Date(seg.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}. Note: Listings are in beta and fares may change.</>
                                                                }
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>


                                            {sharedRoute.stops_data?.[i] && (
                                                <div className="timeline-segment-pro connection">
                                                    <div className="sidebar-visual-pro">
                                                        <div className="node-icon-box-pro small">
                                                            <img src="/icon6.svg" alt="Connection" />
                                                        </div>
                                                        <div className="vertical-line-pro"></div>
                                                    </div>
                                                    <div className="leg-details-container-pro">
                                                        <div className="connection-card-pro">
                                                            <div className="connection-header-pro">
                                                                <span className="connection-title-pro">{sharedRoute.stops_data[i].stop_title}</span>
                                                                {sharedRoute.stops_data[i].layover_cab && (
                                                                    <span className="cab-selected-price">₹{selectedCabPrice(sharedRoute.stops_data[i].layover_cab, cabSel[`l${i}`]).toLocaleString('en-IN')}</span>
                                                                )}
                                                                <span className="connection-tag-pro">Layover</span>
                                                            </div>
                                                            <div className="connection-body-pro">
                                                                <p>
                                                                    {sharedRoute.stops_data[i].commute_comment ||
                                                                        sharedRoute.stops_data[i].exp_time_avl_explanation ||
                                                                        sharedRoute.stops_data[i].exp_time_avl_comment ||
                                                                        sharedRoute.stops_data[i].layover_comment ||
                                                                        sharedRoute.stops_data[i].sch_layover_comment ||
                                                                        sharedRoute.stops_data[i].stop_description ||
                                                                        `Wait for ${formatDuration(sharedRoute.stops_data[i].commute_time_mins || sharedRoute.stops_data[i].sch_time_avl_mins)} between connecting ${sharedRoute.stops_data[i].mode === 'train' ? 'trains' : 'flights'}`
                                                                    }
                                                                </p>
                                                                <CabOptions
                                                                    cab={sharedRoute.stops_data[i].layover_cab}
                                                                    selected={cabSel[`l${i}`]}
                                                                    onSelect={(c) => setCabSel(prev => ({ ...prev, [`l${i}`]: c }))}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {isLast && (sharedRoute.commute_dist || sharedRoute.commute_time_mins || item.segment?.dest_comment_web_title || item.segment?.dest_cab) && (
                                                <div className="timeline-segment-pro connection final">
                                                    <div className="sidebar-visual-pro">
                                                        <div className="node-icon-box-pro small green">
                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M18 15L15 17V21H21V17L18 15ZM23 21C23 21.5304 22.7891 22.039 22.4141 22.4141C22.039 22.7891 21.5304 23 21 23H15C14.4696 23 13.961 22.7891 13.5859 22.4141C13.2109 22.039 13 21.5304 13 21V17C13 16.6709 13.0811 16.3468 13.2363 16.0566C13.3916 15.7664 13.6159 15.5186 13.8896 15.3359H13.8906L16.8896 13.3369V13.3359C17.2183 13.1167 17.6049 13 18 13C18.3951 13 18.7817 13.1167 19.1104 13.3359L19.1094 13.3369L22.1094 15.3359H22.1104L22.21 15.4082C22.4385 15.5819 22.6278 15.8027 22.7637 16.0566C22.9189 16.3468 23 16.6709 23 17V21Z" fill="#16A34A" />
                                                                <path d="M17 10C17 8.14348 16.2629 6.36256 14.9502 5.0498C13.7194 3.819 12.077 3.09452 10.3467 3.00879L10 3C8.14348 3 6.36256 3.73705 5.0498 5.0498C3.73705 6.36256 3 8.14348 3 10C3 12.1593 4.21679 14.4871 5.79785 16.5645C7.32796 18.5748 9.08458 20.2015 10.0039 20.999C10.5539 21.0011 10.9994 21.4474 11 21.998C11.0005 22.5157 10.6076 22.9423 10.1035 22.9941L10.001 22.999L9.83887 22.9922C9.5168 22.9663 9.20594 22.8628 8.93262 22.6904L8.79883 22.5986C8.78059 22.5849 8.76237 22.5706 8.74512 22.5557C7.78983 21.7308 5.88248 19.978 4.20605 17.7754C2.55262 15.603 1 12.8337 1 10C1 7.61305 1.94791 5.32357 3.63574 3.63574C5.32357 1.94791 7.61305 1 10 1C12.3869 1 14.6764 1.94791 16.3643 3.63574C18.0521 5.32357 19 7.61305 19 10C19 10.5523 18.5523 11 18 11C17.4477 11 17 10.5523 17 10Z" fill="#16A34A" />
                                                                <path d="M17 22V19C17 18.4477 17.4477 18 18 18C18.5523 18 19 18.4477 19 19V22C19 22.5523 18.5523 23 18 23C17.4477 23 17 22.5523 17 22Z" fill="#16A34A" />
                                                                <path d="M12 10C12 8.89543 11.1046 8 10 8C8.89543 8 8 8.89543 8 10C8 11.1046 8.89543 12 10 12C11.1046 12 12 11.1046 12 10ZM14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10Z" fill="#16A34A" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                    <div className="leg-details-container-pro">
                                                        <div className="connection-card-pro commute">
                                                            <div className="connection-header-pro">
                                                                <span className="connection-title-pro">{item.segment?.dest_comment_web_title || `Reach ${sharedRoute.dest_city || to}`}</span>
                                                                {item.segment?.dest_cab && (
                                                                    <span className="cab-selected-price">₹{selectedCabPrice(item.segment.dest_cab, cabSel.dest).toLocaleString('en-IN')}</span>
                                                                )}
                                                                <span className="connection-tag-pro">Commute</span>
                                                            </div>
                                                            <div className="connection-body-pro">
                                                                <p className="commute-description-pro">
                                                                    {item.segment?.dest_comment_web ? item.segment.dest_comment_web : (
                                                                        <>
                                                                            {sharedRoute.commute_dist ? `${sharedRoute.commute_dist} kms ` : ''}
                                                                            {sharedRoute.commute_dist && sharedRoute.commute_time_mins ? '• ' : ''}
                                                                            {sharedRoute.commute_time_mins ? `${sharedRoute.commute_time_mins} mins ` : ''}
                                                                            to reach {sharedRoute.dest_city || to}
                                                                        </>
                                                                    )}
                                                                </p>
                                                                <CabOptions
                                                                    cab={item.segment?.dest_cab}
                                                                    selected={cabSel.dest}
                                                                    onSelect={(c) => setCabSel(prev => ({ ...prev, dest: c }))}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                } else {
                                    // Fallback for mapData.segments
                                    const seg = item;
                                    const mode = seg.type || 'cab';
                                    const sNode = seg.nodes[0];
                                    const eNode = seg.nodes[seg.nodes.length - 1];

                                    return (
                                        <div className="timeline-segment-pro" key={i}>
                                            <div className="sidebar-visual-pro">
                                                <div className="node-icon-box-pro">
                                                    {mode === 'flight' && <img src="/icon4.svg" alt="Flight" />}
                                                    {mode === 'train' && <img src="/icon1.svg" alt="Train" />}
                                                    {String(mode).toLowerCase() === 'bus' && <FaBus size={24} color="#16A34A" />}
                                                    {(mode === 'cab' || mode === 'transfer') && <img src="/icon3.svg" alt="Commute" />}
                                                </div>
                                                {!isLast && <div className="vertical-line-pro"></div>}
                                            </div>
                                            <div className="leg-details-container-pro">
                                                <div className={`expanded-leg-card-pro ${mode}-type`}>
                                                    <div className="leg-upper-pro">
                                                        <span className="leg-title-pro">{mode === 'bus' ? `${seg.meta?.travel_name || seg.meta?.service_name || 'Bus'}${seg.meta?.bus_type ? ` · ${seg.meta.bus_type}` : ''}` : (seg.id || mode.toUpperCase())}</span>
                                                        <span className="leg-type-tag-pro">{mode.toUpperCase()}</span>
                                                    </div>
                                                    <div className="leg-middle-pro">
                                                        <div className="leg-stations-pro">
                                                            <div className="leg-station-pro">
                                                                <span className="s-name-pro">{sNode.name}</span>
                                                                <span className="s-main-pro">{sNode.name || seg.from_code}, {formatTime(sNode.dep || sNode.origin_dep_dt)}</span>
                                                            </div>
                                                            <div className="leg-visual-pro">
                                                                <div className="d-line-pro"></div>
                                                            </div>
                                                            <div className="leg-station-pro right">
                                                                <span className="s-name-pro">{eNode.name}</span>
                                                                <span className="s-main-pro">{eNode.name || seg.to_code}, {formatTime(eNode.arr || eNode.dest_arr_dt)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                            })}
                        </div>
                    </div>

                </aside>
                <div className="map-canvas">
                    <div className="mobile-route-badge">
                        {(() => {
                            if (!sharedRoute?.nodes || sharedRoute.nodes.length === 0) {
                                return (
                                    <div className="route-badge">
                                        <span className="city">{from}</span>
                                        <span className="arrow">→</span>
                                        <span className="city">{to}</span>
                                    </div>
                                );
                            }

                            const firstSeg = sharedRoute.nodes[0].segment;
                            const lastSeg = sharedRoute.nodes[sharedRoute.nodes.length - 1].segment;

                            const startText = sharedRoute.user_src_name || (firstSeg?.origin_name ?
                                `${firstSeg.origin_name}${(firstSeg.origin_code_iata || firstSeg.origin_code || firstSeg.seat_avail_ui?.src) ? ` (${firstSeg.origin_code_iata || firstSeg.origin_code || firstSeg.seat_avail_ui?.src})` : ''}` :
                                from);

                            const endText = sharedRoute.user_dest_name || (lastSeg?.dest_name ?
                                `${lastSeg.dest_name}${(lastSeg.dest_code_iata || lastSeg.dest_code || lastSeg.seat_avail_ui?.dest) ? ` (${lastSeg.dest_code_iata || lastSeg.dest_code || lastSeg.seat_avail_ui?.dest})` : ''}` :
                                to);

                            return (
                                <div className="route-badge">
                                    <span className="city">{startText}</span>
                                    <span className="arrow">→</span>
                                    <span className="city">{endText}</span>
                                </div>
                            );
                        })()}
                    </div>
                    <MapContainer center={allPoints.length > 0 ? allPoints[0] : [23.178, 80.052]} zoom={6} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_2pnn_1_d6cd3f7f53dd76674c7307fb" />
                        {mapElements}
                        <MapBoundsHandler points={allPoints} />
                    </MapContainer>
                    <div className="map-legend-overlay">
                        {mapData?.segments?.some(s => s.type === 'flight') && <div className="legend-row"><span className="line flight"></span> Flight</div>}
                        {mapData?.segments?.some(s => s.type === 'train') && <div className="legend-row"><span className="line train"></span> Train</div>}
                        {mapData?.segments?.some(s => s.type === 'bus') && <div className="legend-row"><span className="line" style={{ background: '#16A34A' }}></span> Bus</div>}
                        {mapData?.segments?.some(s => s.type === 'cab' || s.type === 'transfer') && <div className="legend-row"><span className="line cab"></span> Cab</div>}
                        <div className="legend-row"><div className="legend-stopover-icon"><FaExchangeAlt size={10} /></div> Stopover</div>
                    </div>
                </div>
            </div>

            {showLoadingModal && <LoadingModal isOpen={true} />}

            {errorModalData && (
                <FallbackErrorModal
                    isOpen={!!errorModalData}
                    onClose={() => setErrorModalData(null)}
                    title={errorModalData.title}
                    errorMessage={errorModalData.message}
                />
            )}
            <ShareMapModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                routeData={sharedRoute}
                searchMetadata={searchMetadata}
            />
        </div>
    );
};

export default SharedMapPage;
